import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useSingleFlight } from 'hooks/useSingleFlight';
import { StepPage } from 'features/subscription-process/navigation/Steps/StepPage';
import { useGroupContractProcess } from '../contexts/GroupContractProcessContext';
import { GroupContractStepIdentifier } from '../types/enums';
import { ProductsSelector, ProductsSelectorHandle } from '../selectors/ProductsSelector';

/**
 * Calcule l'étape suivante à partir de la liste des étapes côté back.
 * En mode broker, le back omet l'étape COURTIERS de `process.steps`
 * → on tombe naturellement sur Contractualisation.
 */
const computeNextStep = (
  steps: { identifier: string }[] | undefined,
  current: GroupContractStepIdentifier,
  fallback: GroupContractStepIdentifier,
): GroupContractStepIdentifier => {
  const ids = (steps ?? []).map(s => s.identifier as GroupContractStepIdentifier);
  const idx = ids.indexOf(current);
  return idx >= 0 && ids[idx + 1] ? ids[idx + 1] : fallback;
};

export const ProductsStep = () => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);
  const { groupContractId, process, refresh, buildStepRoute } = useGroupContractProcess();
  const navigate = useNavigate();
  const selectorRef = useRef<ProductsSelectorHandle>(null);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveAndGoNext = useCallback(async () => {
    if (!selectorRef.current) return;
    setError(null);
    try {
      await selectorRef.current.save();
      await refresh();
      const nextStep = computeNextStep(
        process?.steps,
        GroupContractStepIdentifier.Products,
        GroupContractStepIdentifier.Contractualization,
      );
      navigate(buildStepRoute(nextStep));
    } catch (err: any) {
      setError(err?.message || t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.ERROR_SAVE'));
    }
  }, [refresh, navigate, buildStepRoute, process, t]);

  // Un seul save en vol : les clics répétés sur "Suivant" sont ignorés
  // (sinon POST /tunnels concurrents → conflit de concurrence côté back).
  const { run: handleNext, isInFlight: isSubmitting } = useSingleFlight(saveAndGoNext);

  return (
    <StepPage
      prevConfig={{
        label: t('GROUP_CONTRACT_PROCESS.ACTIONS.PREVIOUS'),
        onClick: () => navigate(buildStepRoute(GroupContractStepIdentifier.Subscriber)),
      }}
      nextConfig={{
        label: isSubmitting
          ? t('GROUP_CONTRACT_PROCESS.ACTIONS.SAVING')
          : t('GROUP_CONTRACT_PROCESS.ACTIONS.NEXT'),
        onClick: handleNext,
        disabled: !isValid || isSubmitting,
      }}
    >
      <div className="flex flex-col gap-4 max-w-3xl mx-auto">
        <h2 className="text-lg font-semibold">
          {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.TITLE')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('GROUP_CONTRACT_PROCESS.PRODUCTS_STEP.DESCRIPTION')}
        </p>

        <ProductsSelector
          ref={selectorRef}
          groupContractId={groupContractId}
          onValidityChange={setIsValid}
        />

        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>
    </StepPage>
  );
};
